import * as rd from '@devexperts/remote-data-ts'
import { Button } from '@spiaggeit/spit-ui'
import { ArrowRightCircle } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useInterval } from 'usehooks-ts'

import { THANK_YOU_PATH } from '@/app/router/paths.ts'
import { useAppPath } from '@/hooks/useAppPath.ts'
import { licenseSlice } from '@/store/licenseSlice'
import { ONE_MINUTE, REG_ID_QUERY_PARAM_NAME } from '@/utils/constants.ts'
import { KioskTimeoutDialog } from '../../components/Dialog/KioskTimeoutDialog'
import { useAppDispatch, useAppSelector } from '../../hooks/store'
import { useBackLink } from '../../hooks/useBackLink'
import { httpClient } from '../../lib/http/HttpClient'
import { CartResponse, ServicesResponse } from '../../models/cart'
import { appSlice } from '../../store/appSlice'
import { captureError, kioskSlice } from '../../store/kioskSlice'
import { getKioskLocalSettings } from '../../utils/kiosk'
import { printReceipt } from '../../utils/printer'

import { PaymentKioskDialog } from './Dialog'
import {
  KioskBeachServices,
  KioskPaymentPayload,
  KioskPrintFailureFn,
  KioskPrintSuccessFn,
  KioskReservations,
} from './type'

export type PaymentError = 'payment' | 'print'

export const PaymentKioskRoute = () => {
  const { t } = useTranslation()

  // Store
  const dispatch = useAppDispatch()
  const license = useAppSelector(licenseSlice.selectors.license)
  const country = useAppSelector(appSlice.selectors.country)
  const isPaymentComplete = useAppSelector(
    kioskSlice.selectors.isPaymentComplete
  )
  const paymentIntentId = useAppSelector(kioskSlice.selectors.paymentIntentId)
  const kioskSettings = useAppSelector(kioskSlice.selectors.settings)

  // Router
  const navigate = useNavigate()
  const backLink = useBackLink()
  const thankYouPath = useAppPath(THANK_YOU_PATH)
  const [searchParams] = useSearchParams()
  const regId = searchParams.get(REG_ID_QUERY_PARAM_NAME)

  // State
  const [cart, setCart] = useState<KioskReservations>([])
  const [dialogErrorType, setDialogErrorType] = useState<PaymentError | null>(
    null
  )
  const [services, setServices] = useState<KioskBeachServices>({})
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  function showErrorDialog(errorType: PaymentError) {
    setDialogErrorType(errorType)
    setIsDialogOpen(true)
  }

  function hideErrorDialog() {
    setIsDialogOpen(false)
    setDialogErrorType(null)
  }

  /**
   * Setup payment
   * @param payload - Payment payload
   */
  async function setupPayment(payload: KioskPaymentPayload) {
    try {
      const [cartResponse, servicesResponse] = await Promise.all([
        httpClient.fetch<CartResponse>(
          `beaches/${payload.licenseCode}/carts/${regId}`,
          country
        ),
        httpClient.fetch<ServicesResponse>(
          `beaches/${payload.licenseCode}/services`,
          country
        ),
        dispatch(
          kioskSlice.actions.createPaymentIntent({
            cartId: payload.regId,
            terminal: payload.posTerminalId,
          })
        ).unwrap(),
      ])

      if (
        cartResponse.status === 'success' &&
        servicesResponse.status === 'success'
      ) {
        setCart(cartResponse.data.result.cart.reservationModels)
        setServices(servicesResponse.data.result.beachServices)
      } else {
        throw new Error('Failed to fetch cart or services')
      }
    } catch (error) {
      captureError(error)
      showErrorDialog('payment')
    }
  }

  /**
   * Fetch kiosk settings
   */
  useEffect(() => {
    const kioskLocalSettings = getKioskLocalSettings()
    if (!kioskLocalSettings) return

    dispatch(
      kioskSlice.actions.fetchSettings({
        kioskId: kioskLocalSettings.kioskId,
      })
    )
  }, [])

  /**
   * Setup payment page
   */
  useEffect(() => {
    // Setup payment page
    if (!rd.isSuccess(kioskSettings) || !license || !regId) return

    setupPayment({
      licenseCode: license.license,
      posTerminalId: kioskSettings.value.posTerminalId,
      regId,
    })
  }, [kioskSettings, license, regId])

  /**
   * Show error if payment intent or kiosk settings are failed
   */
  useEffect(() => {
    if (rd.isFailure(paymentIntentId) || rd.isFailure(kioskSettings)) {
      showErrorDialog('payment')
    }
  }, [paymentIntentId, kioskSettings])

  /**
   * Print receipt
   */
  useEffect(() => {
    const kioskLocalSettings = getKioskLocalSettings()

    if (
      !isPaymentComplete ||
      !rd.isSuccess(kioskSettings) ||
      !kioskLocalSettings ||
      !regId ||
      !license
    ) {
      return
    }

    setDialogErrorType(null)
    setIsDialogOpen(true)

    dispatch(
      kioskSlice.actions.logPrintStart({
        kioskId: kioskLocalSettings.kioskId,
        reservationId: Number(regId),
      })
    )

    printReceipt(
      {
        ...kioskSettings.value.printer,
        width: kioskSettings.value.printer.invoiceWidth,
      },
      license,
      cart,
      Object.values(services),
      handlePrintSuccess({
        kioskId: kioskLocalSettings.kioskId,
        reservationId: Number(regId),
      }),
      handlePrintFailure({
        kioskId: kioskLocalSettings.kioskId,
        reservationId: Number(regId),
      })
    )
  }, [isPaymentComplete, kioskSettings, regId, license])

  /**
   * Check payment intent status
   */
  const checkPaymentIntentStatus = useCallback(() => {
    if (!rd.isSuccess(paymentIntentId) || !license || isPaymentComplete) return

    dispatch(kioskSlice.actions.checkPaymentIntentStatus())
  }, [paymentIntentId, license, isPaymentComplete])

  useInterval(checkPaymentIntentStatus, 1000)

  /**
   * Navigate to thank you page
   */
  function navigateToThankYou() {
    if (!regId) return

    navigate({
      pathname: thankYouPath,
      search: `?${REG_ID_QUERY_PARAM_NAME}=${regId}`,
    })
  }

  /**
   * Print success
   * @param data - Print data
   */
  const handlePrintSuccess: KioskPrintSuccessFn =
    (data) => (receipt, shouldLog) => {
      if (!regId || !license) return

      if (shouldLog && receipt) {
        dispatch(
          kioskSlice.actions.logPrintSuccess({
            ...data,
            ...receipt,
            receiptDate: receipt.date,
          })
        )
      }
      hideErrorDialog()
      navigateToThankYou()
    }

  /**
   * Print failure
   * @param data - Print data
   */
  const handlePrintFailure: KioskPrintFailureFn =
    (data) => (printError, shouldLog) => {
      if (shouldLog) {
        dispatch(kioskSlice.actions.logPrintFailure(data))
      }
      // eslint-disable-next-line no-console
      console.error(printError)
      captureError(printError)
      showErrorDialog('print')
    }

  /**
   * Abort payment and navigate to back link
   */
  function handlePaymentCancel() {
    if (!backLink) return

    dispatch(kioskSlice.actions.abortPaymentIntent()).then(() => {
      navigate(backLink)
    })
  }

  /**
   * Payment retry
   */
  function handleRetryPayment() {
    if (!regId || !license || !rd.isSuccess(kioskSettings)) {
      return
    }

    hideErrorDialog()
    setupPayment({
      licenseCode: license.license,
      posTerminalId: kioskSettings.value.posTerminalId,
      regId,
    })
  }

  function handleRetryPrint() {
    const kioskLocalSettings = getKioskLocalSettings()

    if (
      !rd.isSuccess(kioskSettings) ||
      !rd.isSuccess(paymentIntentId) ||
      !kioskLocalSettings ||
      !license ||
      !regId
    ) {
      return
    }

    printReceipt(
      {
        ...kioskSettings.value.printer,
        width: kioskSettings.value.printer.invoiceWidth,
      },
      license,
      cart,
      Object.values(services),
      handlePrintSuccess({
        kioskId: kioskLocalSettings.kioskId,
        reservationId: Number(regId),
      }),
      handlePrintFailure({
        kioskId: kioskLocalSettings.kioskId,
        reservationId: Number(regId),
      })
    )
  }

  /**
   * Render payment kiosk
   */
  if (!license || !regId) return null

  return (
    <>
      <div className="flex flex-1 flex-col items-center justify-center gap-4 p-4">
        <img
          className="h-20"
          src={`https://img.spiagge.it/logo/${license.license}.png`}
        />
        <p className="text-center text-sm">{t('kiosk.payment.message')}</p>
        <ArrowRightCircle className="size-8" />
      </div>

      {backLink ? (
        <Button
          className="fixed bottom-6 left-6 rounded border-gray-400 px-4 py-2 font-bold"
          color="error"
          fullWidth={false}
          onClick={handlePaymentCancel}
          type="button"
          variant="outline"
        >
          {t('chooseProduct.kiosk.cancel')}
        </Button>
      ) : null}

      <PaymentKioskDialog
        error={dialogErrorType}
        isOpen={isDialogOpen}
        onPaymentCancel={handlePaymentCancel}
        onPaymentRetry={handleRetryPayment}
        onPrintCancel={navigateToThankYou}
        onPrintRetry={handleRetryPrint}
        regId={regId}
      />

      <KioskTimeoutDialog
        onKill={() => dispatch(kioskSlice.actions.abortPaymentIntent())}
        timeout={ONE_MINUTE * 5}
      />
    </>
  )
}
